/* ═══════════════════════════════════════════
   Nav active-state de-duplication

   Shopify's `link.active` is a URL match, so when several menu entries point at
   the same destination every one of them is marked active and they all light up
   together. Liquid cannot tell them apart — the links are identical as far as
   the server is concerned — so the choice is remembered in the browser: the
   entry the visitor clicked most recently keeps `.active`, its twins lose it.
   The choice is stored in localStorage, so it survives new tabs and later
   visits; only on a first visit, with nothing clicked yet, does the first of
   the twins win.

   Only links that share the same href are ever touched, so a genuinely
   different active link is never affected.
═══════════════════════════════════════════ */
(function () {
  'use strict';

  var STORE_KEY = 'navActiveChoice';
  var NAVS = '.nav-desktop-menu, .accordion-nav';
  var LINKS = '.nav-desktop-menu .nav-link, .accordion-nav .nav-link';

  function keyOf(a) {
    return (a.getAttribute('href') || '') + '|' + (a.textContent || '').trim();
  }

  /* localStorage first so the choice survives a new tab or a later visit;
     sessionStorage is only a fallback where localStorage is blocked. */
  function read() {
    try {
      var v = localStorage.getItem(STORE_KEY);
      if (v) return v;
    } catch (e) {}
    try { return sessionStorage.getItem(STORE_KEY); } catch (e) {}
    return null;
  }

  function write(v) {
    try { localStorage.setItem(STORE_KEY, v); return; } catch (e) {}
    try { sessionStorage.setItem(STORE_KEY, v); } catch (e) {}
  }

  function dedupe(root) {
    if (!root || !root.querySelectorAll) return;
    var stored = read();
    Array.prototype.forEach.call(root.querySelectorAll(NAVS), function (nav) {
      var active = Array.prototype.slice.call(nav.querySelectorAll('.nav-link.active'));
      if (active.length < 2) return;

      /* Group by destination — only same-href twins are ambiguous */
      var byHref = {};
      active.forEach(function (a) {
        var h = a.getAttribute('href') || '';
        (byHref[h] = byHref[h] || []).push(a);
      });

      Object.keys(byHref).forEach(function (href) {
        var group = byHref[href];
        if (group.length < 2) return;

        /* The entry the visitor clicked most recently wins. group[0] is only a
           last resort, for a first visit where nothing has been clicked yet. */
        var keep = null;
        if (stored) {
          keep = group.filter(function (a) { return keyOf(a) === stored; })[0] || null;
        }
        if (!keep) keep = group[0];

        group.forEach(function (a) {
          if (a === keep) return;
          a.classList.remove('active');
          if (a.getAttribute('aria-current') === 'page') a.removeAttribute('aria-current');
        });
      });
    });
  }

  /* Move the highlight onto the clicked entry straight away, rather than waiting
     for the next page load to re-run dedupe. Without this the highlight only
     catches up after a reload, and never at all where storage is blocked (the
     theme editor's preview frame partitions it). */
  function highlightNow(clicked) {
    var nav = clicked.closest(NAVS);
    if (!nav) return;
    var href = clicked.getAttribute('href') || '';
    var twins = Array.prototype.slice.call(nav.querySelectorAll('.nav-link'))
      .filter(function (x) { return (x.getAttribute('href') || '') === href; });
    if (twins.length < 2) return;

    /* Only when this page already highlights that destination — otherwise the
       destination page decides, and we would light up a link wrongly here. */
    var lit = twins.some(function (x) { return x.classList.contains('active'); });
    if (!lit) return;

    twins.forEach(function (x) {
      if (x === clicked) {
        x.classList.add('active');
        x.setAttribute('aria-current', 'page');
      } else {
        x.classList.remove('active');
        if (x.getAttribute('aria-current') === 'page') x.removeAttribute('aria-current');
      }
    });
  }

  /* Remember which of the twins was clicked, before the page navigates */
  document.addEventListener('click', function (e) {
    /* Only a plain primary click navigates this tab. A middle-click or a
       cmd/ctrl-click opens somewhere else and must not change the highlight. */
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!e.target.closest) return;
    var a = e.target.closest(LINKS);
    if (!a) return;
    var href = a.getAttribute('href');
    if (!href || href === '#') return;
    write(keyOf(a));
    highlightNow(a);
  });

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    dedupe(document);
    document.addEventListener('shopify:section:load', function (e) { dedupe(e.target); });
    /* Restored from the back/forward cache: the DOM comes back as it was served,
       with every twin active again. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) dedupe(document); });
  });
})();
